import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { ArrowLeft, ArrowRight, Clock, User } from "lucide-react";
import Seo from "@/components/common/Seo";
import { Reveal } from "@/components/common/Reveal";
import CTASection from "@/components/common/CTASection";
import { api, resolveImageUrl } from "@/lib/api";

const DEST = "success-stories";

const Section = ({ title, body }) => (
  body ? (
    <div className="mt-10">
      <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">{title}</p>
      <div className="mt-3 prose prose-lg prose-slate max-w-none whitespace-pre-line font-body leading-relaxed">
        {body}
      </div>
    </div>
  ) : null
);

export default function SuccessStoryDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/blogs/${slug}`, { params: { destination: DEST } });
        setData(data);
      } catch { setData(null); }
      finally { setLoading(false); }
    })();
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) return <div className="grid min-h-[60vh] place-items-center font-body text-slate-500">Loading…</div>;
  if (!data) return (
    <div className="grid min-h-[60vh] place-items-center text-center">
      <div>
        <h1 className="font-heading text-3xl font-bold text-navy">Story not found</h1>
        <p className="mt-2 font-body text-slate-500">This Executive Success Story may have been moved or unpublished.</p>
        <button onClick={() => nav("/success-stories")} className="mt-6 inline-flex items-center gap-2 rounded-full bg-royal px-5 py-2.5 font-body text-sm font-semibold text-white hover:bg-navy">
          <ArrowLeft className="h-4 w-4" /> Back to Success Stories
        </button>
      </div>
    </div>
  );

  const { blog, prev, next, related } = data;
  const canonical = `https://aftabazad.com/success-stories/${blog.slug}`;
  const heroFull = resolveImageUrl(blog.hero_image);
  const industry = blog.industry || blog.category;

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: blog.title,
    description: blog.meta_description || blog.excerpt || "",
    image: heroFull ? [heroFull] : undefined,
    datePublished: blog.published_at,
    dateModified: blog.updated_at,
    author: { "@type": "Person", name: blog.author, url: "https://aftabazad.com/about" },
    publisher: {
      "@type": "Person", name: "Aftab Azad",
      logo: { "@type": "ImageObject", url: "https://aftabazad.com/favicon-512x512.png" },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://aftabazad.com/" },
      { "@type": "ListItem", position: 2, name: "Success Stories", item: "https://aftabazad.com/success-stories" },
      { "@type": "ListItem", position: 3, name: blog.title, item: canonical },
    ],
  };

  return (
    <>
      <Seo
        title={blog.meta_title || blog.title}
        description={blog.meta_description || blog.excerpt || ""}
        path={`/success-stories/${blog.slug}`}
        image={heroFull}
        type="article"
        keywords={blog.tags}
        jsonLd={[articleLd, breadcrumbLd]}
      />

      <article className="bg-white pt-20 pb-12 md:pt-28">
        <div className="mx-auto max-w-4xl px-6 md:px-10">
          <div className="flex flex-wrap items-center gap-2 font-body text-xs uppercase tracking-[0.25em] text-slate-500">
            <Link to="/" className="hover:text-royal">Home</Link>
            <span>›</span>
            <Link to="/success-stories" className="hover:text-royal">Success Stories</Link>
            <span>›</span>
            <span className="text-navy">{industry}</span>
          </div>
          <h1 data-testid="success-story-title" className="mt-4 font-heading text-3xl font-light leading-[1.1] tracking-tight md:text-5xl">{blog.title}</h1>
          {blog.subtitle && <p className="mt-4 font-body text-lg text-slate-600">{blog.subtitle}</p>}
          <div className="mt-6 flex flex-wrap items-center gap-4 font-body text-sm text-slate-500">
            {blog.published_at && <span>{format(new Date(blog.published_at), "d MMMM yyyy")}</span>}
            <span className="inline-flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> {blog.author}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {blog.reading_time} min read</span>
          </div>
        </div>

        {heroFull && (
          <div className="mt-10 md:mt-14">
            <div className="mx-auto max-w-6xl overflow-hidden bg-slate-100 md:rounded-[1.5rem]">
              <img src={heroFull} alt={blog.title} className="h-[280px] w-full object-cover md:h-[520px]" />
            </div>
          </div>
        )}

        <div className="mx-auto mt-10 max-w-3xl px-6 md:mt-14 md:px-0">
          <Section title="Industry" body={blog.industry} />
          <Section title="Business situation" body={blog.business_situation} />
          <Section title="Executive challenge" body={blog.executive_challenge} />
          <Section title="Approach" body={blog.approach} />
          <Section title="Strategic intervention" body={blog.strategic_intervention} />
          <Section title="Business outcomes" body={blog.business_outcomes} />
          <Section title="Key takeaways" body={blog.key_takeaways} />

          {blog.content && (
            <div className="mt-10">
              <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">The story</p>
              <div data-testid="success-story-content" className="mt-3 prose prose-lg prose-slate max-w-none prose-headings:font-heading prose-headings:tracking-tight prose-h2:text-3xl prose-h3:text-2xl prose-a:text-royal prose-a:no-underline hover:prose-a:underline prose-img:rounded-xl prose-img:border prose-img:border-slate-200" dangerouslySetInnerHTML={{ __html: blog.content }} />
            </div>
          )}

          {blog.cta && (
            <div className="mt-14 rounded-[1.25rem] border border-royal/20 bg-royal/5 p-6 md:p-8">
              <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Next step</p>
              <p className="mt-3 font-heading text-2xl font-light leading-snug text-navy">{blog.cta}</p>
              <Link to="/book" className="mt-5 inline-flex items-center gap-2 rounded-full bg-royal px-5 py-2.5 font-body text-sm font-semibold text-white hover:bg-navy">
                Schedule Executive Strategy Session <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}

          <div className="mt-14 flex flex-wrap gap-4 border-t border-slate-200 pt-8">
            {prev && (
              <Link to={`/success-stories/${prev.slug}`} className="group flex-1 min-w-[240px] rounded-[1rem] border border-slate-200 bg-white p-5 hover:border-royal">
                <p className="font-body text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">← Previous</p>
                <p className="mt-2 font-heading text-lg font-bold text-navy group-hover:text-royal">{prev.title}</p>
              </Link>
            )}
            {next && (
              <Link to={`/success-stories/${next.slug}`} className="group flex-1 min-w-[240px] rounded-[1rem] border border-slate-200 bg-white p-5 text-right hover:border-royal">
                <p className="font-body text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Next →</p>
                <p className="mt-2 font-heading text-lg font-bold text-navy group-hover:text-royal">{next.title}</p>
              </Link>
            )}
          </div>

          {related?.length ? (
            <div className="mt-16">
              <h3 className="font-heading text-2xl font-bold text-navy">Related stories</h3>
              <div className="mt-6 grid gap-6 md:grid-cols-3">
                {related.map((r) => (
                  <Reveal key={r.slug}>
                    <Link to={`/success-stories/${r.slug}`} className="group flex h-full flex-col overflow-hidden rounded-[1.25rem] border border-slate-200 bg-white hover:border-royal">
                      {r.hero_image ? (
                        <div className="h-40 overflow-hidden bg-slate-100"><img src={resolveImageUrl(r.hero_image)} alt={r.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
                      ) : (
                        <div className="flex h-40 items-center justify-center bg-gradient-to-br from-navy via-navy to-royal font-heading text-lg text-white/40">{r.category}</div>
                      )}
                      <div className="flex flex-1 flex-col p-4">
                        <p className="font-body text-xs font-bold uppercase tracking-[0.2em] text-royal">{r.category}</p>
                        <h4 className="mt-2 font-heading text-base font-bold text-navy group-hover:text-royal">{r.title}</h4>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </article>

      <CTASection
        overline="Talk with Aftab"
        heading={["Explore what a similar", "outcome could mean", "for your business."]}
        body="Every engagement begins with an executive strategy conversation. No sales pitch, just senior counsel on the decisions that matter."
        primary={{ label: "Schedule Executive Strategy Session", to: "/book" }}
        secondary={{ label: "Contact Aftab", to: "/contact" }}
      />
    </>
  );
}
